import { useEffect, useRef, useCallback, useState } from 'react';
import trackingService, {
  CourseEnrollment,
  LessonProgress,
  ResumePoint,
} from '../services/trackingService';

interface UseTrackingOptions {
  lessonId: number;
  lessonTitle: string;
  courseId?: number;
  userId: number;
  userEmail: string;
}

interface UseTrackingReturn {
  progress: LessonProgress | null;
  enrollment: CourseEnrollment | null;
  isLoading: boolean;
  error: Error | null;
  startLesson: () => Promise<void>;
  completeLesson: (result?: { score?: number; success?: boolean }) => Promise<void>;
  updateProgress: (
    progressPercentage: number,
    data?: { score?: string; suspendData?: any }
  ) => Promise<void>;
}

/**
 * Hook for tracking lesson progress and sending xAPI statements
 */
export function useTracking(options: UseTrackingOptions): UseTrackingReturn {
  const {
    lessonId,
    lessonTitle,
    courseId,
    userId,
    userEmail,
  } = options;

  const [progress, setProgress] = useState<LessonProgress | null>(null);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const hasStartedRef = useRef(false);

  // Initialize tracking
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        // Set userId in tracking service
        trackingService.setUserId(userId);

        if (courseId) {
          setEnrollment(await trackingService.getEnrollment(courseId));
        }

        // Get existing lesson progress
        const existingProgress = await trackingService.getLessonProgress(lessonId);
        setProgress(existingProgress);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [lessonId, courseId]);

  // Start the lesson
  const startLesson = useCallback(async () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    try {
      // Update lesson progress
      const updatedProgress = await trackingService.updateLessonProgress(lessonId, {
        status: 'in_progress',
      });
      setProgress(updatedProgress);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [lessonId, lessonTitle, courseId, userId, userEmail]);

  // Complete the lesson
  const completeLesson = useCallback(
    async (result?: { score?: number; success?: boolean }) => {
      try {
        // Mark lesson as complete
        const updatedProgress = await trackingService.completeLesson(lessonId);
        setProgress(updatedProgress);

        // Reload enrollment to get updated progress
        if (courseId) {
          const updatedEnrollment = await trackingService.getEnrollment(courseId);
          setEnrollment(updatedEnrollment);
        }
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [lessonId, lessonTitle, courseId, userId, userEmail]
  );

  // Update progress
  const updateProgress = useCallback(
    async (
      progressPercentage: number,
      data?: { score?: string; suspendData?: any }
    ) => {
      try {
        const updatedProgress = await trackingService.updateLessonProgress(lessonId, {
          progressPercentage,
          ...data,
        });
        setProgress(updatedProgress);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [lessonId]
  );

  return {
    progress,
    enrollment,
    isLoading,
    error,
    startLesson,
    completeLesson,
    updateProgress,
  };
}

/**
 * Hook for getting resume point for a course
 */
export function useResumePoint(courseId: number, userId?: number) {
  const [resumePoint, setResumePoint] = useState<ResumePoint | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchResumePoint = async () => {
      try {
        setIsLoading(true);
        // Set userId in tracking service if provided
        if (userId) {
          trackingService.setUserId(userId);
        }
        const data = await trackingService.getResumePoint(courseId);
        setResumePoint(data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchResumePoint();
  }, [courseId, userId]);

  return { resumePoint, isLoading, error };
}

/**
 * Hook for video position tracking with resume support.
 * Periodically saves the current video position to suspendData
 * so users can resume where they left off.
 */
export function useVideoTracking(
  lessonId: number,
  videoTitle: string,
  userId: number,
  userEmail: string
) {
  const lastSaveTimeRef = useRef<number>(0);
  const lastPositionRef = useRef<number>(0);
  const isSavingRef = useRef(false);
  const resumePositionRef = useRef<number | null>(null);
  const [resumePosition, setResumePosition] = useState<number | null>(null);

  // Load existing progress to get resume position
  useEffect(() => {
    const loadResumePosition = async () => {
      try {
        trackingService.setUserId(userId);
        const progress = await trackingService.getLessonProgress(lessonId);
        if (progress?.suspendData?.videoPosition) {
          const pos = Number(progress.suspendData.videoPosition);
          if (pos > 0) {
            resumePositionRef.current = pos;
            setResumePosition(pos);
          }
        }
      } catch {
        // No existing progress - that's fine
      }
    };
    loadResumePosition();
  }, [lessonId, userId]);

  // Save position to API (throttled - at most every 10 seconds)
  const savePosition = useCallback(
    async (currentTime: number, duration: number) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      try {
        await trackingService.updateLessonProgress(lessonId, {
          suspendData: {
            videoPosition: Math.floor(currentTime),
            videoDuration: Math.floor(duration),
            lastUpdated: new Date().toISOString(),
          },
          progressPercentage: duration > 0 ? Math.min(Math.round((currentTime / duration) * 100), 100) : 0,
        });
      } catch {
        // Silently fail - will retry on next interval
      } finally {
        isSavingRef.current = false;
      }
    },
    [lessonId]
  );

  // Called frequently (e.g. on timeupdate), but only saves every 10 seconds
  const trackProgress = useCallback(
    (currentTime: number, duration: number) => {
      lastPositionRef.current = currentTime;
      const now = Date.now();
      if (now - lastSaveTimeRef.current >= 10000) {
        lastSaveTimeRef.current = now;
        savePosition(currentTime, duration);
      }
    },
    [savePosition]
  );

  // Force-save current position immediately (for pause/leave events)
  const savePositionNow = useCallback(
    (currentTime: number, duration: number) => {
      lastSaveTimeRef.current = Date.now();
      savePosition(currentTime, duration);
    },
    [savePosition]
  );

  return { trackProgress, savePositionNow, resumePosition };
}

export default useTracking;
